import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCreditCard, FiLock, FiArrowLeft, FiCheck } from 'react-icons/fi';

const CheckoutContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 0;
`;

const CheckoutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CheckoutHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const CheckoutTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const FormSection = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin-top: 2px;
`;

const CheckboxLabel = styled.label`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  
  a {
    color: #2563eb;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const OrderItems = styled.div`
  margin-bottom: 20px;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const OrderItemImage = styled.div`
  width: 50px;
  height: 70px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OrderItemInfo = styled.div`
  flex: 1;
`;

const OrderItemTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const OrderItemDetails = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 4px;
`;

const OrderItemPrice = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.125rem;
  }
`;

const SummaryLabel = styled.span`
  color: #6b7280;
`;

const SummaryValue = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const PlaceOrderButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 12px;
  text-align: center;
`;

const Checkout = ({ cart, user, isAuthenticated }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: user?.phone || '',
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: '',
    billingAddress: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate order processing
    setTimeout(() => {
      alert('Order placed successfully! Thank you for your purchase.');
      navigate('/');
    }, 2000);
  };

  if (!isAuthenticated) {
    return (
      <CheckoutContainer>
        <CheckoutContent>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <h2>Please sign in to continue</h2>
            <p>You need to be logged in to complete your purchase.</p>
            <Link to="/login" style={{ color: '#2563eb', textDecoration: 'none' }}>
              Sign In
            </Link>
          </div>
        </CheckoutContent>
      </CheckoutContainer>
    );
  }

  if (cart.length === 0) {
    return (
      <CheckoutContainer>
        <CheckoutContent>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <h2>Your cart is empty</h2>
            <p>Add some books to your cart before checking out.</p>
            {/* <Link to="/books" style={{ color: '#2563eb', textDecoration: 'none' }}>
              Browse Books
            </Link> */}
          </div>
        </CheckoutContent>
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer>
      <CheckoutContent>
        <CheckoutHeader>
          <BackButton to="/cart">
            <FiArrowLeft />
            Back to Cart
          </BackButton>
          <CheckoutTitle>Checkout</CheckoutTitle>
        </CheckoutHeader>

        <CheckoutGrid>
          <CheckoutForm>
            <form onSubmit={handleSubmit}>
              <FormSection>
                <SectionTitle>
                  <FiCreditCard />
                  Payment Information
                </SectionTitle>
                
                <FormGrid>
                  <InputGroup>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>Billing Address</SectionTitle>
                
                <FormGrid>
                  <InputGroup>
                    <Label>First Name</Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>Last Name</Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </FormGrid>
                
                <InputGroup>
                  <Label>Street Address</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>
                
                <FormGrid>
                  <InputGroup>
                    <Label>City</Label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>State</Label>
                    <Input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </FormGrid>
                
                <FormGrid>
                  <InputGroup>
                    <Label>ZIP Code</Label>
                    <Input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>Country</Label>
                    <Select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Australia">Australia</option>
                    </Select>
                  </InputGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>Payment Method</SectionTitle>
                
                <FormGrid>
                  <InputGroup>
                    <Label>Card Number</Label>
                    <Input
                      type="text"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      placeholder="1234 5678 9012 3456"
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>Name on Card</Label>
                    <Input
                      type="text"
                      name="nameOnCard"
                      value={formData.nameOnCard}
                      onChange={handleChange}
                      required
                    />
                  </InputGroup>
                </FormGrid>
                
                <FormGrid>
                  <InputGroup>
                    <Label>Expiry Date</Label>
                    <Input
                      type="text"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      placeholder="MM/YY"
                      required
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>CVV</Label>
                    <Input
                      type="text"
                      name="cvv"
                      value={formData.cvv}
                      onChange={handleChange}
                      placeholder="123"
                      required
                    />
                  </InputGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>Order Notes</SectionTitle>
                <TextArea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any special instructions for your order..."
                />
              </FormSection>

              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                />
                <CheckboxLabel htmlFor="terms">
                  I agree to the <Link to="/terms" onClick={scrollToTop}>Terms of Service</Link> and{' '}
                  <Link to="/privacy" onClick={scrollToTop}>Privacy Policy</Link>
                </CheckboxLabel>
              </CheckboxGroup>

              <PlaceOrderButton type="submit" disabled={loading || !agreedToTerms}>
                <FiLock />
                {loading ? 'Processing...' : 'Place Order'}
              </PlaceOrderButton>
            </form>
          </CheckoutForm>

          <OrderSummary>
            <h3>Order Summary</h3>
            
            <OrderItems>
              {cart.map((item) => (
                <OrderItem key={item.id}>
                  <OrderItemImage>
                    <img src={item.image} alt={item.title} />
                  </OrderItemImage>
                  <OrderItemInfo>
                    <OrderItemTitle>{item.title}</OrderItemTitle>
                    <OrderItemDetails>by {item.author}</OrderItemDetails>
                    <OrderItemDetails>Qty: {item.quantity}</OrderItemDetails>
                    <OrderItemPrice>${(item.price * item.quantity).toFixed(2)}</OrderItemPrice>
                  </OrderItemInfo>
                </OrderItem>
              ))}
            </OrderItems>
            
            <SummaryRow>
              <SummaryLabel>Subtotal ({cart.length} item{cart.length !== 1 ? 's' : ''})</SummaryLabel>
              <SummaryValue>${calculateSubtotal().toFixed(2)}</SummaryValue>
            </SummaryRow>
            
            <SummaryRow>
              <SummaryLabel>Tax</SummaryLabel>
              <SummaryValue>${calculateTax().toFixed(2)}</SummaryValue>
            </SummaryRow>
            
            <SummaryRow>
              <SummaryLabel>Total</SummaryLabel>
              <SummaryValue>${calculateTotal().toFixed(2)}</SummaryValue>
            </SummaryRow>

            <SecurityNote>
              <FiLock size={16} />
              Your payment information is secure and encrypted
            </SecurityNote>
          </OrderSummary>
        </CheckoutGrid>
      </CheckoutContent>
    </CheckoutContainer>
  );
};

export default Checkout;
